import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { useAlert } from '../components/AlertModal';
import { generateJan } from "../utils/jan"; 
import { useReturnParams } from '../hooks/useReturnParams';
import { printLabelAndDeleteById, addLabelAndPrintQr } from "../components/PriceLabels"; 
import "../styles/InventoryDetail.css";
import InventoryAdjustmentList from "./InventoryAdjustmentList";

const ProductDetail = () => {
  const { id, storeid } = useParams(); // URLパラメータから商品IDを取得
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [summaryitem, setSummaryItem] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [storeId, setStoreId] = useState(1);
  const { showAlert } = useAlert();
  const [salesHistory, setSalesHistory] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [showImageConfirm, setShowImageConfirm] = useState(false);
  const [stockLocked, setStockLocked] = useState(true);
  const [relatedItems, setRelatedItems] = useState([]);
  const [variationItems, setVariationItems] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const { getReturnParams, clearReturnParams } = useReturnParams();
  const [deleteUnlocked, setDeleteUnlocked] = useState(false);
  const [setRelations, setSetRelations] = useState({
    as_parent: [],
    as_component: [],
  });
  const [newComponentId, setNewComponentId] = useState("");

  const itemControllerRef = useRef(null);
  const salesHistoryControllerRef = useRef(null);
  const purchaseHistoryControllerRef = useRef(null);
  const relatedControllerRef = useRef(null);
  const variationControllerRef = useRef(null);
  const summaryControllerRef = useRef(null);
  const stockUpdateTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      itemControllerRef.current?.abort();
      salesHistoryControllerRef.current?.abort();
      purchaseHistoryControllerRef.current?.abort();
      relatedControllerRef.current?.abort();
      variationControllerRef.current?.abort();
      summaryControllerRef.current?.abort();
    };
  }, []);



  let blurInProgress = false;
  
  // セットデータの取得
  const fetchSetRelations = useCallback(async () => {
    if (!id || !storeId) return;

    try {
      const res = await apiRequest(
        `inventory/item_set_relations/${id}/${storeId}/`,
        "GET"
      );
      setSetRelations(res);
    } catch (err) {
      console.error("セット関連取得失敗:", err);
    }
  }, [id, storeId]);

  useEffect(() => {
    fetchSetRelations();
  }, [fetchSetRelations]);

  // 商品データの取得（更新後にも使う）
  const fetchItem = useCallback(async () => {
    itemControllerRef.current?.abort();
    const controller = new AbortController();
    itemControllerRef.current = controller;

    try {
      const data = await apiRequest(
        `inventory/inventory/${id}/`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );
      setItem(data);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error('商品データの取得に失敗しました:', error);
    }
  }, [id]);
  
  useEffect(() => {
    if (item?.sku) {
      setImageUrl(`/media/product_images/${item.sku}.jpg?${Date.now()}`);
    }
  }, [item?.sku]);
  
  // 販売履歴
  const fetchSalesHistory = useCallback(async () => {
    salesHistoryControllerRef.current?.abort();
    const controller = new AbortController();
    salesHistoryControllerRef.current = controller;

    try {
      const res = await apiRequest(
        `inventory/sales_history/${id}/${storeid}/`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );
      setSalesHistory(res.latest_sales);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("販売履歴取得失敗:", error);
    }
  }, [id, storeid]);
  
  // 買取履歴
  useEffect(() => {
    purchaseHistoryControllerRef.current?.abort();
    const controller = new AbortController();
    purchaseHistoryControllerRef.current = controller;

    const fetchPurchaseHistory = async () => {
      try {
        const res = await apiRequest(
          `inventory/purchase_history/${id}/`,
          "GET",
          null,
          {},
          true,
          controller.signal
        );
        setPurchaseHistory(res);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("買取履歴取得失敗:", err);
      }
    };

    fetchPurchaseHistory();

    return () => controller.abort();
  }, [id]);

  // セット関連商品取得
  useEffect(() => {
    if (!item?.id) return;

    relatedControllerRef.current?.abort();
    const controller = new AbortController();
    relatedControllerRef.current = controller;

    const seen = new Set();
    const merged = [];

    const fetchAll = async () => {
      try {
        const [relatedRes, componentRes, coSoldRes] = await Promise.all([
          apiRequest(
            `inventory/related_set_products/${item.id}/${storeId}/`,
            "GET",
            null,
            {},
            true,
            controller.signal
          ),
          apiRequest(
            `inventory/components_of_set/${item.id}/${storeId}/`,
            "GET",
            null,
            {},
            true,
            controller.signal
          ),
          apiRequest(
            `inventory/co_sold_items/${item.id}/${storeId}/`,
            "GET",
            null,
            {},
            true,
            controller.signal
          ),
        ]);

        const allItems = [
          ...(relatedRes.related_products || []),
          ...(componentRes.components || []),
          ...(coSoldRes.co_sold_items || []),
        ];

        allItems.forEach((p) => {
          if (!seen.has(p.inventory_id)) {
            seen.add(p.inventory_id);
            merged.push(p);
          }
        });

        setRelatedItems(merged);
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error("関連商品の取得失敗", e);
      }
    };

    fetchAll();

    return () => controller.abort();
  }, [item?.id, storeId]);

  // バリエーション商品取得
  useEffect(() => {
    if (!item?.etc) return;

    variationControllerRef.current?.abort();
    const controller = new AbortController();
    variationControllerRef.current = controller;

    apiRequest(
      `inventory/related_versions/${item.id}/`,
      "GET",
      null,
      {},
      true,
      controller.signal
    )
      .then(setVariationItems)
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("バリエーション取得失敗:", err);
      });

    return () => controller.abort();
  }, [item?.etc, item?.id]);

  const fetchSalesPurchaseSummary = useCallback(async () => {
    summaryControllerRef.current?.abort();
    const controller = new AbortController();
    summaryControllerRef.current = controller;

    try {
      const query = `store_id=${storeId}&inventory_id=${id}`;
      const res = await apiRequest(
        `inventory/sales/summary/?${query}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );

      const item = res.results?.[0] || {};

      setSummaryItem({
        sales_summary: item.sales_summary || {},
        purchase_summary: item.purchase_summary || {},
        single_sales_summary: item.single_sales_summary || {},
        set_sales_summary: item.set_sales_summary || {},

        sales_average_summary: item.sales_average_summary || {},
        single_sales_average_summary: item.single_sales_average_summary || {},
        set_sales_average_summary: item.set_sales_average_summary || {},
        purchase_average_summary: item.purchase_average_summary || {},
      });

      console.log("✅ summary:", item);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("販売・仕入集計の取得に失敗:", error);
    }
  }, [id, storeId]);
 
  useEffect(() => {
    setItem(null);
    setStoreId(Number(storeid)); // storeidを数値に変換してsetStoreIdを呼び出す
    fetchItem();
    fetchSalesHistory();
    fetchSalesPurchaseSummary();
  }, [id,fetchItem, storeid, fetchSalesHistory, fetchSalesPurchaseSummary]); // 初回のみ
  
  const getStoreInventory = (item) =>
    item.store_inventory_set?.find((inv) => inv.store === storeId);

   
  const getCompetitorStore = (item) =>
    item.competitorstore_set?.find((inv) => inv.store_name === "駿河屋");

  const handleChange = (id, field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleBlur = async (item) => {
  if (blurInProgress) return; // 多重実行防止
  blurInProgress = true;

  try {
    const edited = editValues[item.id];
    if (!edited) {
      blurInProgress = false;
      return;
    }

    const inventoryUpdates = {};
    const storeInventoryUpdates = {};
    const storeInv = getStoreInventory(item) || {};
    const surugaya = getCompetitorStore(item);

    for (const key in edited) {
      if ([
          "jan","search_jan", "item_name", "manufacturer", "sku",
          "title", "genre1", "genre2", "category_name",
          "picture_switch", "csv_switch",
          "furigana", "manufacturer_furigana", "release_date",
          "title_abb", "title_abb2", "etc", "note"
      ].includes(key)) {
        if (edited[key] !== item[key]) {
          inventoryUpdates[key] = edited[key];
        }
      } else if (key === "competitor_productid") {
        if (edited[key] !== surugaya?.competitor_productid) {
          await handleSurugayaIdUpdate(surugaya?.id, item.id, edited[key]);
        }
      } else {
        if (edited[key] !== storeInv[key]) {
          storeInventoryUpdates[key] = edited[key];
        }
      }
    }

    // 片方だけ更新でも非同期順番を守るために await
    if (Object.keys(inventoryUpdates).length > 0) {
      await handleInventoryUpdate(item.id, inventoryUpdates);
    }
    if (Object.keys(storeInventoryUpdates).length > 0) {
      await submitStoreInventoryUpdate(item.id, storeInventoryUpdates, storeInv?.id);
    }
  } catch (err) {
    console.error("handleBlur error:", err);
  } finally {
    blurInProgress = false;
  }
};
  // 商品マスター更新
  const handleInventoryUpdate = async (itemId, updates) => {
    try {
      await apiRequest(`inventory/inventory/${itemId}/`, 'PATCH', updates);
      setEditValues((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      fetchItem();
    } catch (error) {
      console.error("Inventory更新失敗:", error);
    }
  };
  // 在庫関連データ更新
  const submitStoreInventoryUpdate = async (itemId, updates, storeInventoryId) => {
    const isNew = !storeInventoryId;
    const endpoint = isNew ? "inventory/storeinventory/" : `inventory/storeinventory/${storeInventoryId}/`;
    const method = isNew ? "POST" : "PATCH";
    const storeInv = getStoreInventory(item)

    const payload = {
      ...(isNew ? { inventory: itemId, store: storeId } : {}),
      ...updates,
      version_buy: storeInv?.version_buy ?? 1,
    };

    try {
      await apiRequest(endpoint, method, payload);
      setEditValues((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      fetchItem();
    } catch (error) {
      if  (error.status === 409) {
        // 最新取得して上書き
        showAlert("他で変更された可能性があります。最新状態で上書きしました。");
        const latestItem = await apiRequest(`inventory/inventory/${itemId}/`);
        const latestStoreInv = latestItem.store_inventory_set.find((inv) => inv.store === storeId);

        await apiRequest(endpoint, method, {
          ...payload,
          version_buy: latestStoreInv.version_buy,  // 最新バージョンで上書き
        });
        fetchItem();
        setEditValues((prev) => {
          const newState = { ...prev };
          delete newState[itemId];
          return newState;
        });
      } else {
        console.error("StoreInventory更新失敗:", error);
      }
    }
  };
  // 競合店ID更新
  const handleSurugayaIdUpdate = async (competitorId, itemId, value) => {
    try {
      if (competitorId) {
        await apiRequest(`inventory/competitorstore/${competitorId}/`, 'PATCH', {
          competitor_productid: value
        });
        fetchItem();
      } else {
        await apiRequest(`inventory/competitorstore/`, 'POST', {
          store_name: "駿河屋",
          inventory: itemId,
          competitor_productid: value,
        });
        fetchItem();
      }
    } catch (error) {
      console.error("駿河屋ID更新失敗:", error);
    }
  };

  const handleCheckboxChange_I = (item, field, checked) => {
    const updates = { [field]: checked };
    handleInventoryUpdate(item.id, updates);
  };

  const handleCheckboxChange = (item, field, checked) => {
    const storeInv = getStoreInventory(item);
    const storeInventoryId = storeInv?.id;
    const updates = { [field]: checked };
    submitStoreInventoryUpdate(item.id, updates, storeInventoryId);
  };

  const handleUpdateStock = async (item, storeId, newStock) => {
    const storeInventory = getStoreInventory(item);
    const itemId = item.id;
    const currentStock = Number(storeInventory?.stock ?? 0);
    const targetStock = Number(newStock);
    const version = storeInventory?.version ?? 1;

    if (Number.isNaN(targetStock)) return;
    if (targetStock === currentStock) return;

    try {
      await apiRequest("inventory/inventory_adjustments/", "POST", {
        inventory: itemId,
        store_id: storeId,
        target_stock: targetStock,
        reason: "在庫調整",
        version: version,
      });

      fetchItem();
    } catch (error) {
      if (error?.status === 409 || String(error?.message).includes("409")) {
        showAlert("在庫数が他で更新されました。最新の在庫を取得します。");
        fetchItem();
      } else {
        console.error("在庫上書き失敗:", error);
      }
    }
  };

  const handleDelayedStockUpdate = (item, storeId, value) => {
    if (stockUpdateTimerRef.current) {
      clearTimeout(stockUpdateTimerRef.current);
    }

    stockUpdateTimerRef.current = setTimeout(() => {
      handleUpdateStock(item, storeId, Number(value));
    }, 800);
  };

  // 削除
  const handleDelete = async () => {
    const confirmed = window.confirm("本当にこの商品を削除しますか？\n履歴がある場合は非表示（論理削除）になります。");
    if (!confirmed) return;

    try {
      const res = await apiRequest(`inventory/inventory/${item.id}/`, "DELETE");
      showAlert(res?.message || "削除しました");
      navigate(-1);
    } catch (error) {
      console.error("削除エラー:", error);
      showAlert("削除に失敗しました（商品が存在しないか、すでに削除された可能性があります）");
    }
  };
  const handleSurugayaImagePreviewFetch = async (sku, surugayaId) => {
    if (!sku || !surugayaId) {
      showAlert("SKUまたは駿河屋IDが空です。");
      return;
    }
  
    try {
      const res = await apiRequest("inventory/preview_surugaya_image/", "POST", {
        sku,
        surugaya_id: surugayaId,
      });
  
      if (res.preview_base64) {
        setPreviewImage(`data:image/jpeg;base64,${res.preview_base64}`);
        setShowImageConfirm(true); // モーダル表示
      } else {
        showAlert("プレビュー画像の取得に失敗しました");
      }
    } catch (err) {
      console.error("プレビュー取得失敗:", err);
      showAlert("画像取得に失敗しました");
    }
  };

  // JAN作成
  const handgenerateJan = async (id,jan) => {
        if (jan===""  ) {
          handleChange(id, "jan", generateJan(id))
          const updates = { "jan": generateJan(id) };
          handleInventoryUpdate(id, updates);
        }else{
          showAlert("JANが既にあります。削除してから押してください。");
        }
    };

  // フォーカス時に全選択   
  const handleFocus = (e) => {
    e.target.select();  // フォーカス時に全選択
  };  


  
  
  // 戻るボタンのクリックで前のページに戻る
  const handleBack = () => {
    const returnUrl = getReturnParams();
    if (returnUrl) {
      clearReturnParams();  // 一度戻ったらクリア
      navigate(returnUrl);
    } else {
      navigate(-1); // フォールバック
    }
  };

  // バリエーション番号自動生成
  const handleGetNewVersionCode = async () => {
    try {
      const res = await apiRequest("inventory/get_next_version_code/");
      const newCode = res.next_version_code;
      handleChange(item.id, "etc", newCode);
      showAlert(`新しいバージョンコード ${newCode} を設定しました`);
    } catch (err) {
      console.error("バージョンコード取得失敗:", err);
      showAlert("バージョンコードの取得に失敗しました");
    }
  };

  // バリエーションラベル抽出  
  const extractVariationLabel = (etc) => {
    if (!etc) return "";
    const match = etc.match(/^Ver\d{5}(.*)$/); // 先頭Ver00001の後ろ全部を取る
    const label = match?.[1]?.trim();
    return label || ""; // 空文字なら何も表示しない
  };
  
  // 商品データがまだ取得されていない場合のローディング表示
  if (!item) {
    return <div>読み込み中...</div>;
  }

  // ラベル追加
  const handleAddToLabel = async (id, price) => {
      try {
        const response = await apiRequest(`inventory/add_to_label/`, "POST", {
          id,
          price,
        });
  
        if (response.message) {
          showAlert(response.message);
        }
        
      } catch (error) {
        console.error("Error:", error);
        showAlert("エラーが発生しました");
      }
    };
  // セット構成
  const handleAddSetComponent = async () => {
    if (!newComponentId) {
      showAlert("構成品の商品IDを入力してください");
      return;
    }

    try {
      const res = await apiRequest("inventory/item_set_components/add/", "POST", {
        set_inventory_id: item.id,
        component_inventory_id: Number(newComponentId),
        quantity: 1,
      });

      showAlert(res.message || "追加しました");
      setNewComponentId("");
      fetchSetRelations();
    } catch (err) {
      console.error("セット構成品追加失敗:", err);
      showAlert(err?.details?.error || "追加に失敗しました");
    }
  };

  const handleDeleteSetComponent = async (componentId) => {
    if (!window.confirm("この構成品を削除しますか？")) return;

    try {
      const res = await apiRequest(
        `inventory/item_set_components/${componentId}/delete/`,
        "DELETE"
      );

      showAlert(res?.message || "削除しました");
      fetchSetRelations();
    } catch (err) {
      console.error("セット構成品削除失敗:", err);
      showAlert("削除に失敗しました");
    }
  };    
  
  return (
    <>
    <div className="product-detail-header">
      <button className='inventoryDetail-backButton' onClick={handleBack}>戻る</button><h2>(ID:{item.id}) {item.item_name} の詳細</h2>
    </div>  
    <div className="product-detail-container-scroll">
      <div className="product-detail-container">
        <div className="product-detail-left">
          <div className="detail">
              <table>
                <tbody>
                  <tr>
                    <td className="detail_header"><strong>商品名</strong></td>
                    <td>
                      <textarea
                        type="text"
                        value={editValues[item.id]?.item_name ?? item.item_name ?? ""}
                        onChange={(e) => handleChange(item.id, "item_name", e.target.value)}
                        onBlur={() => handleBlur(item)}
                        rows={3}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="detail_header"><strong>フリガナ</strong></td>
                    <td>
                      <textarea
                        type="text"
                        value={editValues[item.id]?.furigana ?? item.furigana ?? ""}
                        onChange={(e) => handleChange(item.id, "furigana", e.target.value)}
                        onBlur={() => handleBlur(item)}
                        rows={1}
                      />
                    </td>
                  </tr>
              
                </tbody>
              </table>
            </div>
          <div className="detail-left">
            <div className="detail">
              <table>
                <tbody>
                  <tr>
                    <td colSpan="2">
                      <div className="product-detail-imgbox">
                        <img
                          src={imageUrl}
                          alt={item.item_name}
                          className="PopBoxImageSmall"
                        />

                        <button
                          className="image-reload-btn"
                          onClick={() => {
                            setImageUrl(`/media/product_images/${item.sku}.jpg?ts=${Date.now()}`);
                          }}
                          title="画像リロード"
                        >
                          🔄
                        </button>
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>SKU</strong></td>
                    <td>
                      <input
                        type="text"
                        value={editValues[item.id]?.sku ?? item.sku ?? ""}
                        onChange={(e) => handleChange(item.id, "sku", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>

                  

                  <tr>
                    <td className="detail_header"><strong>メーカー</strong></td>
                    <td>
                      <input
                        type="text"
                        value={editValues[item.id]?.manufacturer ?? item.manufacturer ?? ""}
                        onChange={(e) => handleChange(item.id, "manufacturer", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>メーカー(カナ)</strong></td>
                    <td>
                      <input
                        type="text"
                        value={editValues[item.id]?.manufacturer_furigana ?? item.manufacturer_furigana ?? ""}
                        onChange={(e) => handleChange(item.id, "manufacturer_furigana", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header" onClick={() => handgenerateJan(item.id,editValues[item.id]?.jan ?? item.jan?? "")}><strong><span>JANコード</span></strong></td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                      <input
                        type="text"
                        value={editValues[item.id]?.jan ?? item.jan ?? ""}
                        onChange={(e) => handleChange(item.id, "jan", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                      <button
                        className="detail_print"
                        onClick={async () => {
                          try {
                            const itemData = await apiRequest(`inventory/inventory/${item.id}/`, "GET");
                            printLabelAndDeleteById(item, item.price, showAlert);
                          } catch (err) {
                            console.error("JAN印刷エラー:", err);
                            showAlert("JAN印刷に失敗しました");
                          }
                        }}
                      >
                        JAN
                      </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="detail_header"><strong>検索用JAN/型番</strong></td>
                    <td>
                      <input
                        type="text"
                        value={editValues[item.id]?.search_jan ?? item.search_jan ?? ""}
                        onChange={(e) => handleChange(item.id, "search_jan", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="detail_header"><strong>発売日</strong></td>
                    <td>
                      <input
                        type="date"
                        value={editValues[item.id]?.release_date ?? item.release_date ?? ""}
                        onChange={(e) => handleChange(item.id, "release_date", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>タイトル</strong></td>
                    <td>
                      <input
                        type="text"
                        value={editValues[item.id]?.title ?? item.title ?? ""}
                        onChange={(e) => handleChange(item.id, "title", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>検索ワード</strong></td>
                    <td>
                      <textarea
                        type="text"
                        value={editValues[item.id]?.title_abb ?? item.title_abb ?? ""}
                        onChange={(e) => handleChange(item.id, "title_abb", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>検索ワード2</strong></td>
                    <td>
                      <textarea
                        type="text"
                        value={editValues[item.id]?.title_abb2 ?? item.title_abb2 ?? ""}
                        onChange={(e) => handleChange(item.id, "title_abb2", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>ジャンル1</strong></td>
                    <td>
                      <input
                        type="text"
                        value={editValues[item.id]?.genre1 ?? item.genre1 ?? ""}
                        onChange={(e) => handleChange(item.id, "genre1", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>ジャンル2</strong></td>
                    <td>
                      <input
                        type="text"
                        value={editValues[item.id]?.genre2 ?? item.genre2 ?? ""}
                        onChange={(e) => handleChange(item.id, "genre2", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>バリエーション情報</strong></td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <button onClick={handleGetNewVersionCode} disabled={!!(editValues[item.id]?.etc ?? item.etc)} className="version-code-button">新規</button>
                        <input
                          type="text"
                          value={editValues[item.id]?.etc ?? item.etc ?? ""}
                          onChange={(e) => handleChange(item.id, "etc", e.target.value)}
                          onBlur={() => handleBlur(item)}
                        />
                      </div>
                    </td>
                  </tr>
                  
                  {/* Inventory側のスイッチ */}
                  <tr>
                    <td className="detail_header"><strong>画像スイッチ</strong></td>
                    <td>
                      <input
                        type="checkbox"
                        checked={item.picture_switch ?? false}
                        onChange={(e) =>
                          handleCheckboxChange_I(item, "picture_switch", e.target.checked)
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>CSVスイッチ</strong></td>
                    <td>
                      <input
                        type="checkbox"
                        checked={item.csv_switch ?? false}
                        onChange={(e) =>
                          handleCheckboxChange_I(item, "csv_switch", e.target.checked)
                        }
                      />
                    </td>
                  </tr>

                  {/* 駿河屋ID */}
                  <tr>
                    <td className="detail_header"><strong>駿河屋ID</strong>               
                    </td>
                    <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                      <input
                        type="text"
                        defaultValue={getCompetitorStore(item)?.competitor_productid ?? ""}
                        onBlur={(e) =>
                          handleSurugayaIdUpdate(
                            getCompetitorStore(item)?.id,
                            item.id,
                            e.target.value
                          )
                        }
                      />
                      <button
                        className="surugayaimage-btn-small"
                        onClick={() =>
                          handleSurugayaImagePreviewFetch(
                            item.sku,
                            getCompetitorStore(item)?.competitor_productid
                          )
                        }
                      >
                        📷
                      </button>
                    </div> 
                    </td>
                  </tr>
                </tbody>
              </table>
                <div>
                  {/* ロック解除ボタン */}
                  <span
                        onClick={() => setDeleteUnlocked(prev => !prev)}
                        style={{ cursor: "pointer", marginLeft: "8px" }}
                        title={deleteUnlocked ? "削除ロック中（クリックで解除）" : "削除可能（クリックでロック）"}
                      >
                        {deleteUnlocked ? "🔓" : "🔒"}
                  </span>
                  {/* 削除ボタン */}
                  <button
                    className="inventoryDetail-deleteButton"
                    onClick={handleDelete}
                    disabled={!deleteUnlocked} // ロック解除されるまでは押せない
                  >
                    商品を削除
                  </button>
                </div>
            </div>
            <div className="detail">
              <div className="detail_buttonbox">
                <a href={`https://www.amazon.co.jp/s?k=${item.jan}`} target="_blank" rel="noopener noreferrer">
                  <button className="detail_amozon_button">AM</button>
                </a>
                <a href={`https://slist.amiami.jp/top/search/list?s_keywords=${item.jan}`} target="_blank" rel="noopener noreferrer">  
                  <button className="detail_amiami_button_s">AS</button>
                </a>
                <a href={`https://slist.amiami.jp/top/search/list9?s_keywords=${item.jan}&submit=%E6%A4%9C%E7%B4%A2&pagemax=20&s_buy_flg=1`} target="_blank" rel="noopener noreferrer">
                  <button className="detail_amiami_button_b">AB</button>
                </a>
                <a
                  href={`https://www.suruga-ya.jp/search?category=&search_word=${
                    editValues[item.id]?.competitor_productid
                      ?? item.competitorstore_set?.find((inv) => inv.store_name === "駿河屋")?.competitor_productid
                      ?? (
                        !item.jan || item.jan === ""
                          ? item.item_name
                          : item.jan.length === 18
                            ? item.jan.slice(-9)
                            : item.jan.startsWith("20137")
                              ? item.item_name
                              : item.jan
                      )
                  }&searchbox=1`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button className="detail_surugaya_button_s">SS</button>
                </a> 
                <a
                  href={`https://www.suruga-ya.jp/kaitori/search_buy?category=&search_word=${
                    editValues[item.id]?.competitor_productid
                      ?? item.competitorstore_set?.find((inv) => inv.store_name === "駿河屋")?.competitor_productid
                      ?? (
                        !item.jan || item.jan === ""
                          ? item.item_name
                          : item.jan.length === 18
                            ? item.jan.slice(-9)
                            : item.jan.startsWith("20137")
                              ? item.item_name
                              : item.jan
                      )
                  }&searchbox=1`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button className="detail_surugaya_button_b">SB</button>
                </a>                
              </div>
              <table>
                <tbody>
                  <tr>
                    <td className="detail_header"><strong>販売価格</strong><button className="detail_print" onClick={() => handleAddToLabel(item.id,(item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0))}>追加</button></td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                      <input
                        type="number"
                        value={editValues[item.id]?.sell_price ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0)}
                        onChange={(e) => handleChange(item.id, "sell_price", e.target.value, storeId)}              
                        onBlur={() => handleBlur(item)}
                        onFocus={handleFocus}
                      />
                      <button
                        className="detail_print"
                        onClick={() =>
                          addLabelAndPrintQr(item.id, storeId, showAlert)
                        }
                      >
                        QR
                      </button>
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>セット販売価格</strong></td>
                    <td>
                      <input
                        type="number"
                        value={editValues[item.id]?.sell_set_price ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_set_price ?? 0)}
                        onChange={(e) => handleChange(item.id, "sell_set_price", e.target.value, storeId)}
                        onBlur={() => handleBlur(item)}
                        onFocus={handleFocus}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header">
                      <strong>在庫数</strong>
                      <span
                        onClick={() => setStockLocked(prev => !prev)}
                        style={{ cursor: "pointer", marginLeft: "8px" }}
                        title={stockLocked ? "編集ロック中（クリックで解除）" : "編集可能（クリックでロック）"}
                      >
                        {stockLocked ? "🔒" : "🔓"}
                      </span>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={
                          editValues[item.id]?.stock ??
                          item.store_inventory_set?.find(inv => inv.store === storeId)?.stock ?? 0
                        }
                        readOnly={stockLocked}
                        onChange={(e) => {
                          handleChange(item.id, "stock", Number(e.target.value));
                          handleDelayedStockUpdate(item, storeId, e.target.value);
                        }}
                        onFocus={handleFocus}
                      />
                    </td>
                  </tr>           

                  <tr>
                    <td className="detail_header"><strong>買取価格</strong></td>
                    <td>
                      <input
                        type="number"
                        value={editValues[item.id]?.buy_price ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_price ?? 0)}
                        onChange={(e) => handleChange(item.id, "buy_price", e.target.value, storeId)}
                        onBlur={() => handleBlur(item)}
                        onFocus={handleFocus}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>特別買取価格</strong></td>
                    <td>
                      <input
                        type="number"
                        value={editValues[item.id]?.buy_specialprice ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_specialprice?? 0)}
                        onChange={(e) => handleChange(item.id, "buy_specialprice", e.target.value, storeId)}
                        onBlur={() => handleBlur(item)}
                        onFocus={handleFocus}
                      />
                    </td>
                  </tr>

                  {/* StoreInventory側のスイッチ */}
                  <tr>
                    <td className="detail_header"><strong>特別スイッチ</strong></td>
                    <td>
                      <input
                        type="checkbox"
                        id={`spbuyswitch${item.id}`} 
                        checked={editValues[item.id]?.buy_specialprice_switch ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_specialprice_switch ?? false)}
                        onChange={(e) => {
                          handleCheckboxChange(item, "buy_specialprice_switch", e.target.checked, storeId); // ← 直接即時更新
                        }}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>特別買取(店限)</strong></td>
                    <td>
                      <input
                        type="checkbox"
                        id={`spmiseswitch${item.id}`} 
                        checked={editValues[item.id]?.buy_specialprice_shoponly ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_specialprice_shoponly ?? false)}
                        onChange={(e) => {
                          handleCheckboxChange(item, "buy_specialprice_shoponly", e.target.checked, storeId);
                        }}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>TOPスイッチ</strong></td>
                    <td>
                      <input
                        type="checkbox"
                        id={`topswitch${item.id}`} 
                        checked={editValues[item.id]?.buy_top_switch ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_top_switch ?? false)}
                        onChange={(e) => {
                          handleCheckboxChange(item, "buy_top_switch", e.target.checked, storeId);
                        }}  
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="detail_header"><strong>在庫スイッチ</strong></td>
                    <td>
                      <input
                        type="checkbox"
                        id={`sstockswitch${item.id}`} 
                        checked={editValues[item.id]?.scale_stock_switch ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.scale_stock_switch ?? false)} 
                        onChange={(e) => {
                          handleCheckboxChange(item, "scale_stock_switch", e.target.checked);
                        }}
                      />
                    </td>
                  </tr>

                  
                  {/* 更新日時（readonly） */}
                  <tr>
                    <td className="detail_header"><strong>更新日時</strong></td>
                    <td>
                      <input
                      type="text"
                      value={editValues[item.id]?.update_date ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.update_date ?? "")}
                      onChange={(e) => handleChange(item.id, "update_date", e.target.value, storeId)}
                      onBlur={() => handleBlur(item)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="note_table">
                <table>
                  <tbody>
                    <tr><td className="detail_header"><strong>備考欄</strong></td></tr>
                    <tr><td>
                      <textarea 
                        className="note_textarea"
                        type="text"
                        value={editValues[item.id]?.note ?? item.note ?? ""}
                        onChange={(e) => handleChange(item.id, "note", e.target.value)}
                        onBlur={() => handleBlur(item)}
                      />
                    </td></tr>
                  </tbody>
                </table>
              </div>
            
            </div>    
          </div>
        </div> 
        <div className="product-detail-right">
          <div className="product-detail-rightbox1">
            {summaryitem && (
              <div className="summaryitem-container">
                <div className="summaryitem-header">
                <span>各集計 </span>
                  <div className="summaryitem-header-number">1W</div>
                  <div className="summaryitem-header-number">1M</div>
                  <div className="summaryitem-header-number">3M</div>
                  <div className="summaryitem-header-number">6M</div>
                  <div className="summaryitem-header-number">12M</div>             
                </div>
                <div className="SalesItemCard-row">
                  <span>販売合計:</span>
                  {["1week", "1month", "3month", "6month", "12month"].map(
                    (key) => (
                      <div key={key} className="SalesItemCard-box">
                        <div>{summaryitem.sales_summary?.[key] ?? 0}</div>
                        <small>¥{(summaryitem.sales_average_summary?.[key] ?? 0).toLocaleString()}</small>
                      </div>
                    )
                  )}
                </div>
                <div className="SalesItemCard-row">
                  <span>単品販売:</span>
                  {["1week", "1month", "3month", "6month", "12month"].map(
                    (key) => (
                      <div key={key} className="SalesItemCard-box">
                        <div>{summaryitem.single_sales_summary?.[key] ?? 0}</div>
                        <small>¥{(summaryitem.single_sales_average_summary?.[key] ?? 0).toLocaleString()}</small>
                      </div>
                    )
                  )}
                </div>
                <div className="SalesItemCard-row">
                  <span>セット売:</span>
                  {["1week", "1month", "3month", "6month", "12month"].map(
                    (key) => (
                      <div key={key} className="SalesItemCard-box">
                        <div>{summaryitem.set_sales_summary?.[key] ?? 0}</div>
                        <small>¥{(summaryitem.set_sales_average_summary?.[key] ?? 0).toLocaleString()}</small>
                      </div>
                    )
                  )}
                </div>
                <div className="SalesItemCard-row">
                  <span>仕入合計:</span>
                  {["1week", "1month", "3month", "6month", "12month"].map(
                    (key) => (
                      <div key={key} className="SalesItemCard-box">
                        <div>{summaryitem.purchase_summary?.[key] ?? 0}</div>
                        <small>¥{(summaryitem.purchase_average_summary?.[key] ?? 0).toLocaleString()}</small>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
            <div className="sales-history-box">
              <span style={{ color: "#0059ff" }}><strong>販売履歴（最新10件）</strong></span>
              <ul>
                {salesHistory.map((sale, idx) => {
                  const returnedQty = Number(sale.returned_quantity || 0);
                  const soldQty = Number(sale.quantity || 0);

                  return (
                    <li key={idx}>
                      {sale.sale_datetime}　{sale.unit_price}円［{sale.sale_type}］

                      {returnedQty > 0 && (
                        <strong style={{ color: "#ff3b30", marginLeft: "6px" }}>
                          {returnedQty >= soldQty
                            ? "【返品】"
                            : `【一部返品 ${returnedQty}個】`
                          }
                        </strong>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="purchase-history-box">
              <span style={{ color: "#d36104ff" }}><strong>買取履歴（最新15件）</strong></span>
              <ul>
                {purchaseHistory.map((p, idx) => (
                  <li key={idx}>
                    {p.date}　{p.unit_price}円 × {p.quantity}個
                  </li>
                ))}
              </ul>
            </div>
            <div><InventoryAdjustmentList inventoryId={item.id} /></div>
          </div>
          <div className="product-detail-rightbox2">
            {variationItems.length > 0 && (
                <div className="variation-section">
                  同一商品のバージョン違い
                  <ul>
                    {variationItems.map((ver) => (
                      <li key={ver.id} onClick={() => navigate(`/inventory_detail/${ver.id}/${storeId}`)} className="variation-item">
                        <img src={`/media/product_images/${ver.sku}.jpg?${Date.now()}`} alt=""
                          onError={(e) => e.target.src = "/media/system_images/noimage.jpg"} />
                        <div className="info">
                          {extractVariationLabel(ver.etc) && `（${extractVariationLabel(ver.etc)}）`}
                          <div className="name">ID: {ver.id}</div>
                          <div className="meta">在庫: {ver.store_inventory_set.find(inv => inv.store === storeId)?.stock ?? 0 }</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
            )}
            <div className="variation-section">
              買取セット構成

              <div style={{ display: "flex", gap: "4px", marginTop: "8px" }}>
                <input
                  type="number"
                  value={newComponentId}
                  onChange={(e) => setNewComponentId(e.target.value)}
                  placeholder="構成品ID"
                />

                <button onClick={handleAddSetComponent}>
                  追加
                </button>
              </div>

              {setRelations.as_parent?.map((group) => (
                <div key={`parent-${group.parent.inventory_id}`}>
                  <div className="meta">この商品の構成品</div>

                  <ul>
                    {group.components.map((comp) => (
                      <li
                        key={comp.component_id}
                        onClick={() => navigate(`/inventory_detail/${comp.inventory_id}/${storeId}`)}
                        className="variation-item"
                      >
                        <img
                          src={`/media/product_images/${comp.sku}.jpg?${Date.now()}`}
                          alt=""
                          onError={(e) => (e.target.src = "/media/system_images/noimage.jpg")}
                        />

                        <div className="info">
                          <div className="name">ID: {comp.inventory_id}</div>
                          <div className="meta">在庫: {comp.stock ?? 0}</div>
                          <div className="meta">{comp.item_name}</div>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSetComponent(comp.component_id);
                          }}
                        >
                          削除
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {setRelations.as_component?.map((group) => (
                <div key={`component-${group.parent.inventory_id}`}>
                  <div className="meta">セット買取表示用</div>

                  <ul>
                    <li
                      onClick={() =>
                        navigate(`/inventory_detail/${group.parent.inventory_id}/${storeId}`)
                      }
                      className="variation-item parent-set-item"
                    >
                      <img
                        src={`/media/product_images/${group.parent.sku}.jpg?${Date.now()}`}
                        alt=""
                        onError={(e) =>
                          (e.target.src = "/media/system_images/noimage.jpg")
                        }
                      />

                      <div className="info">
                        <div className="name">
                          買取用セット ID: {group.parent.inventory_id}
                        </div>
                        <div className="meta">
                          {group.parent.item_name}
                        </div>
                      </div>
                    </li>
                  </ul>

                  <div className="meta">セット構成品</div>

                  <ul>
                    {group.components.map((comp) => (
                      <li
                        key={comp.component_id}
                        onClick={() =>
                          navigate(`/inventory_detail/${comp.inventory_id}/${storeId}`)
                        }
                        className={`variation-item ${
                          comp.is_current ? "is-current-set-component" : ""
                        }`}
                      >
                        <img
                          src={`/media/product_images/${comp.sku}.jpg?${Date.now()}`}
                          alt=""
                          onError={(e) =>
                            (e.target.src = "/media/system_images/noimage.jpg")
                          }
                        />

                        <div className="info">
                          <div className="name">
                            ID: {comp.inventory_id}
                            {comp.is_current ? "（現在の商品）" : ""}
                          </div>
                          <div className="meta">在庫: {comp.stock ?? 0}</div>
                          <div className="meta">{comp.item_name}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {relatedItems.length > 0 && (
              <div className="related-set-section">
                関連商品（セットや一緒に販売された商品を含む）
                <ul>
                  {relatedItems.map((p) => (
                    <li key={p.inventory_id} onClick={() => navigate(`/Inventory_detail/${p.inventory_id}/${storeId}`)}>
                      <img src={`/media/product_images/${p.sku}.jpg?${Date.now()}`} alt=""
                          onError={(e) => e.target.src = "/media/system_images/noimage.jpg"} />
                      <div className="info">
                        <div className="name">ID: {p.inventory_id}</div>
                        <div className="meta">在庫: {p.stock ?? 0}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
        </div>
      </div>
    </div>
    {showImageConfirm && (
      <div className="surugayaimage-modal-overlay">
        <div className="surugayaimage-modal-content">
          <h3>この画像で上書きしますか？</h3>
          <img src={previewImage} alt="プレビュー画像" />
          <div className="surugayaimage-modal-buttons">
          <button onClick={async () => {
            const res = await apiRequest("inventory/confirm_surugaya_image/", "POST", { sku: item.sku });
            console.log(res);
            setShowImageConfirm(false);
            setImageUrl(`/media/product_images/${item.sku}.jpg?${Date.now()}`);
            fetchItem();
            showAlert("画像を上書き保存しました");
          }}>✅ 上書き</button>
          <button onClick={() => setShowImageConfirm(false)}>❌ キャンセル</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ProductDetail;
